import { useState } from 'react'

// Freeform string-list editor — used for international approvals (countries,
// regions, territory names — heterogeneous on purpose, matching the source
// JSON's mixed shape). Deduplicates on add so the wire-shape stays clean.

interface Props {
  label: string
  values: string[]
  placeholder?: string
  onChange: (next: string[]) => void
  readOnly?: boolean
}

export default function StringList({ label, values, placeholder, onChange, readOnly = false }: Props) {
  const [draft, setDraft] = useState('')

  function add() {
    // Accept a single value or a comma/newline-separated list (paste-friendly).
    // Country/region names never legitimately contain a comma, so splitting is
    // safe for every place this input is used. Trim, drop empties, dedupe.
    const parts = draft.split(/[,\n]/).map(s => s.trim()).filter(Boolean)
    if (parts.length === 0) return
    const next = [...values]
    for (const p of parts) {
      if (!next.includes(p)) next.push(p)
    }
    onChange(next.sort())
    setDraft('')
  }

  function remove(v: string) {
    onChange(values.filter(x => x !== v))
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      add()
    }
  }

  return (
    <div className={`ad-list${readOnly ? ' is-read-only' : ''}`}>
      <div className="ad-list-head">
        <span className="b-mono">{label}</span>
        <span className="b-mono-dim b-tiny">{values.length} ITEM{values.length === 1 ? '' : 'S'}</span>
      </div>
      {!readOnly && (
        <div className="ad-list-add">
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder={placeholder ?? 'Add region…'}
            className="ad-list-input"
          />
          <button type="button" onClick={add} disabled={!draft.trim()} className="b-btn b-btn-tiny">ADD</button>
        </div>
      )}
      {values.length > 0
        ? (
            <ul className="ad-list-items">
              {values.map(v => (
                <li key={v} className="ad-list-item">
                  <span className="b-mono">{v}</span>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => remove(v)}
                      className="ad-list-remove"
                      aria-label={`Remove ${v}`}
                    >
                      ✕
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )
        : (
            <p className="b-mono-dim ad-list-empty">none</p>
          )}
    </div>
  )
}
